import { Stack, TextField, Typography } from "@mui/material";

import { LockedDomainAdornment } from "./LockedDomainFromEmailField/LockedDomainAdornment";
import {
  combineLocalWithDomain,
  localPartFromEmail,
  normalizeLocalInput,
} from "./LockedDomainFromEmailField/lockedDomainFromEmailUtils";

import { fieldSx } from "@/features/BroadcastV2/components/sections/sectionStyles";

export interface BroadcastEmailDetailsFieldsProps {
  fromName: string;
  onFromNameChange: (value: string) => void;
  /** Full from email; when lockedDomain is set only the local part is edited. */
  fromEmail: string;
  onFromEmailChange: (value: string) => void;
  replyTo: string;
  onReplyToChange: (value: string) => void;
  /** Domain rendered as a fixed suffix on the From Email field. */
  lockedDomain?: string;
  disabled?: boolean;
  fromNameError?: string;
  fromEmailError?: string;
  replyToError?: string;
}

/** The From Name / From Email / Reply-to field trio, shared by the
 * BroadcastV2 compose form and the Marketing Calendar plan wizard. */
export function BroadcastEmailDetailsFields({
  fromName,
  onFromNameChange,
  fromEmail,
  onFromEmailChange,
  replyTo,
  onReplyToChange,
  lockedDomain = "",
  disabled = false,
  fromNameError = "",
  fromEmailError = "",
  replyToError = "",
}: BroadcastEmailDetailsFieldsProps) {
  const localPart = localPartFromEmail(fromEmail);

  return (
    <>
      <Stack direction="row" gap={4}>
        <Stack gap={1} sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            From Name
          </Typography>
          <TextField
            value={fromName}
            onChange={(e) => onFromNameChange(e.target.value)}
            placeholder="Enter from name"
            disabled={disabled}
            fullWidth
            sx={fieldSx}
            error={fromNameError !== ""}
            helperText={fromNameError || undefined}
          />
        </Stack>

        <Stack gap={1} sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            From Email
          </Typography>
          <TextField
            value={lockedDomain ? localPart : fromEmail}
            onChange={(e) => {
              if (!lockedDomain) {
                onFromEmailChange(e.target.value);
                return;
              }
              const next = normalizeLocalInput(e.target.value);
              onFromEmailChange(combineLocalWithDomain(next, lockedDomain));
            }}
            placeholder="news"
            disabled={disabled}
            fullWidth
            slotProps={{
              input: {
                endAdornment: lockedDomain ? (
                  <LockedDomainAdornment domain={lockedDomain} />
                ) : undefined,
              },
            }}
            sx={fieldSx}
            error={fromEmailError !== ""}
            helperText={fromEmailError || undefined}
          />
        </Stack>
      </Stack>

      <Stack gap={1}>
        <Typography variant="body2" color="text.secondary">
          Reply-to Email
        </Typography>
        <TextField
          value={replyTo}
          onChange={(e) => onReplyToChange(e.target.value)}
          placeholder="Enter reply-to email"
          disabled={disabled}
          fullWidth
          sx={fieldSx}
          error={replyToError !== ""}
          helperText={replyToError || undefined}
        />
      </Stack>
    </>
  );
}
